import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabasePublic } from '../lib/supabasePublic';
import { 
  User, 
  Trophy, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Star
} from 'lucide-react';
import { formatPhoneNumber } from '../utils/formatters';
import { gerarCaracteristicasPorNivel, nivelParaNota } from '../utils/playerUtils';
import { FaWhatsapp } from 'react-icons/fa';

interface Group {
  id: string;
  nome: string;
  tipo: string;
  criado_por: string;
} 

interface PlayerRegistrationPageProps {}

const PublicPlayerRegistrationPage: React.FC<PlayerRegistrationPageProps> = () => {
  const { token } = useParams<{ token: string }>();
  
  const [group, setGroup] = useState<Group | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form states
  const [nome, setNome] = useState('');
  const [idade, setIdade] = useState('');
  const [tipo, setTipo] = useState('ativo');
  const [posicao, setPosicao] = useState('');
  const [referencia, setReferencia] = useState('');
  const [telefone, setTelefone] = useState('');
  const [nivel, setNivel] = useState(3);
  const [countryCode, setCountryCode] = useState('+55');

  const [avatarColor] = useState(() => {
    const colors = [
      'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
      'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  });

  useEffect(() => {
    const fetchGroupData = async () => {
      if (!token) {
        setError('Token de grupo não fornecido');
        setLoading(false);
        return;
      }

      try {
        // Buscar grupo pelo token público
        const { data: groupData, error: groupError } = await supabasePublic
          .from('grupos')
          .select('id, nome, tipo, criado_por')
          .eq('public_registration_token', token)
          .single();

        if (groupError) {
          if (groupError.code === 'PGRST116') {
            setError('Página de cadastro não encontrada ou token inválido');
          } else {
            throw groupError;
          }
          setLoading(false);
          return;
        }

        if (!groupData) {
          setError('Grupo não encontrado ou token inválido');
          setLoading(false);
          return;
        }

        setGroup(groupData);
      } catch (error: any) {
        console.error('Error fetching group data:', error);
        setError('Erro ao carregar dados do grupo');
      } finally {
        setLoading(false);
      }
    };

    fetchGroupData();
  }, [token]);

  const formatPhoneNumber = (value: string, countryCode: string): string => {
    const numbers = value.replace(/\D/g, '');
    
    if (countryCode === '+55') {
      // Brazilian format: (11) 99999-9999
      if (numbers.length <= 11) {
        return numbers
          .replace(/(\d{2})(\d)/, '($1) $2')
          .replace(/(\d{4,5})(\d{4})$/, '$1-$2');
      }
    } else if (countryCode === '+1') {
      // US format: (555) 123-4567
      if (numbers.length <= 10) {
        return numbers
          .replace(/(\d{3})(\d)/, '($1) $2')
          .replace(/(\d{3})(\d{4})$/, '$1-$2');
      }
    } else {
      // European format: simple grouping
      if (numbers.length <= 12) {
        return numbers
          .replace(/(\d{2,3})(\d{3})(\d{3,4})$/, '$1 $2 $3');
      }
    }
    
    return numbers;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTelefone(formatPhoneNumber(e.target.value, countryCode));
  };

  const getPhonePlaceholder = (countryCode: string) => {
    switch (countryCode) {
      case '+55':
        return '(11) 99999-9999';
      case '+1':
        return '(555) 123-4567';
      case '+34':
      case '+33':
      case '+49':
      case '+39':
      case '+44':
      case '+31':
      case '+32':
      case '+41':
      case '+43':
      case '+351':
        return '12 345 6789';
      default:
        return '123456789';
    }
  };

  const validateForm = () => {
    if (!nome.trim()) return 'Nome é obrigatório';
    if (!idade || parseInt(idade) <= 0) return 'Idade inválida';
    if (!posicao) return 'Posição é obrigatória';
    if (!telefone) return 'Telefone é obrigatório';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!group) {
      setError('Dados do grupo não encontrados');
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Verificar se já existe um jogador com este nome no grupo
      const { data: existingPlayer, error: checkError } = await supabasePublic
        .from('jogadores')
        .select('id, nome')
        .eq('grupo_id', group.id)
        .ilike('nome', nome.trim())
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking existing player:', checkError);
        throw new Error('Erro ao verificar jogador existente');
      }

      if (existingPlayer) {
        setError('Já existe um jogador com este nome neste grupo');
        setSubmitting(false);
        return;
      }

      // Gerar características baseadas no nível
      const nota = nivelParaNota(nivel);
      const caracteristicas = gerarCaracteristicasPorNivel(nota);

      // Format phone number for storage (international format)
      const cleanPhone = telefone.replace(/\D/g, '');
      const formattedPhoneForStorage = cleanPhone ? `${countryCode} ${cleanPhone}` : null;
      // Criar jogador
      const { data: newPlayer, error: playerError } = await supabasePublic
        .from('jogadores')
        .insert({
          nome: nome.trim(),
          idade: parseInt(idade),
          tipo,
          posicao,
          referencia: referencia.trim() || null,
          telefone: formattedPhoneForStorage,
          nivel,
          grupo_id: group.id,
          ativo: true,
          caracteristicas: caracteristicas
        })
        .select()
        .single();

      if (playerError) {
        console.error('Error creating player:', playerError);
        throw new Error('Erro ao cadastrar jogador');
      }

      console.log('Player created successfully:', newPlayer);

      // Reset form
      setNome('');
      setIdade('');
      setTipo('ativo');
      setPosicao('');
      setReferencia('');
      setTelefone('');
      setNivel(3);
      setCountryCode('+55');
      setSuccess(true);

      // Hide success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (error: any) {
      console.error('Error submitting player registration:', error);
      setError(error.message || 'Erro ao cadastrar jogador. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  // Preview das características baseado no nível selecionado
  const previewCaracteristicas = gerarCaracteristicasPorNivel(nivelParaNota(nivel));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Carregando página de cadastro...</p>
        </div>
      </div>
    );
  }

  if (error && !group) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark p-4">
        <div className="card p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Página não encontrada</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {error}
          </p>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark p-4">
        <div className="card p-8 max-w-md text-center">
          <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Grupo não encontrado</h1>
          <p className="text-gray-500 dark:text-gray-400">
            O grupo que você está procurando não existe ou foi removido.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark">
      {/* Header */}
      <div className="bg-white dark:bg-dark-paper shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
              <Trophy className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Cadastro de Jogador</h1>
              <p className="text-gray-500 dark:text-gray-400">
                {group.nome} - {group.tipo.charAt(0).toUpperCase() + group.tipo.slice(1)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="card p-6">
          {success && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              <p>Jogador cadastrado com sucesso!</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Avatar Preview */}
            <div className="text-center">
              <div className={`w-32 h-32 rounded-full flex items-center justify-center text-3xl text-white mx-auto ${avatarColor}`}>
                {nome.charAt(0).toUpperCase() || <User className="w-10 h-10" />}
              </div>
            </div>

            <div>
              <label htmlFor="nome" className="block text-sm font-medium mb-1">
                Nome Completo *
              </label>
              <input
                type="text"
                id="nome"
                className="input w-full"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Digite o nome completo"
                required
              />
            </div>

            <div>
              <label htmlFor="idade" className="block text-sm font-medium mb-1">
                Idade *
              </label>
              <input
                type="number"
                id="idade"
                className="input w-full"
                value={idade}
                onChange={(e) => setIdade(e.target.value)}
                min="1"
                max="100"
                placeholder="Digite a idade"
                required
              />
            </div>

            <div>
              <label htmlFor="tipo" className="block text-sm font-medium mb-1">
                Tipo de Jogador *
              </label>
              <select
                id="tipo"
                className="input w-full"
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                required
              >
                <option value="ativo">Ativo</option>
                <option value="avulso">Avulso</option>
              </select>
              <p className="text-sm text-gray-500 mt-1">
                Ativo: Jogador regular do time | Avulso: Jogador eventual
              </p>
            </div>

            <div>
              <label htmlFor="posicao" className="block text-sm font-medium mb-1">
                Posição *
              </label>
              <select
                id="posicao"
                className="input w-full"
                value={posicao}
                onChange={(e) => setPosicao(e.target.value)}
                required
              >
                <option value="">Selecione uma posição</option>
                <option value="goleiro">Goleiro</option>
                <option value="zagueiro">Zagueiro</option>
                <option value="lateral">Lateral</option>
                <option value="meio-campo">Meio Campo</option>
                <option value="atacante">Atacante</option>
              </select>
            </div>

            <div>
              <label htmlFor="nivel" className="block text-sm font-medium mb-1">
                Nível *
              </label>
              <select
                id="nivel"
                className="input w-full"
                value={nivel}
                onChange={(e) => setNivel(parseInt(e.target.value))}
                required
              >
                <option value="1">1 - Iniciante</option>
                <option value="2">2 - Básico</option>
                <option value="3">3 - Intermediário</option>
                <option value="4">4 - Avançado</option>
                <option value="5">5 - Profissional</option>
              </select>
              
              {/* Preview das características */}
              <div className="mt-3 p-3 bg-gray-50 dark:bg-dark-light rounded-lg">
                <p className="text-sm font-medium mb-2">Preview das Características:</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(previewCaracteristicas).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-sm">
                      <span className="capitalize">{key}:</span>
                      <span className="font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="telefone" className="block text-sm font-medium mb-1">
                Telefone (WhatsApp) *
              </label>
              <div className="flex gap-2">
                <select
                  className="input w-32"
                  value={countryCode}
                  onChange={(e) => {
                    setCountryCode(e.target.value);
                    // Reformat phone number when country code changes
                    if (telefone) {
                      setTelefone(formatPhoneNumber(telefone.replace(/\D/g, ''), e.target.value));
                    }
                  }}
                >
                  <option value="+55">🇧🇷 +55</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+54">🇦🇷 +54</option>
                  <option value="+56">🇨🇱 +56</option>
                  <option value="+57">🇨🇴 +57</option>
                  <option value="+51">🇵🇪 +51</option>
                  <option value="+58">🇻🇪 +58</option>
                  <option value="+598">🇺🇾 +598</option>
                  <option value="+595">🇵🇾 +595</option>
                  <option value="+591">🇧🇴 +591</option>
                  <option value="+593">🇪🇨 +593</option>
                  <option value="+34">🇪🇸 +34</option>
                  <option value="+33">🇫🇷 +33</option>
                  <option value="+49">🇩🇪 +49</option>
                  <option value="+39">🇮🇹 +39</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+31">🇳🇱 +31</option>
                  <option value="+32">🇧🇪 +32</option>
                  <option value="+41">🇨🇭 +41</option>
                  <option value="+43">🇦🇹 +43</option>
                  <option value="+351">🇵🇹 +351</option>
                </select>
                <div className="relative flex-1">
                  <FaWhatsapp className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
    
                  <input
                    type="tel"
                    id="telefone"
                    className="input w-full pl-10"
                    value={telefone}
                    onChange={handlePhoneChange}
                    placeholder={getPhonePlaceholder(countryCode)}
                    required
                  />
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Número completo com código do país para WhatsApp
              </p>
            </div>

            <div>
              <label htmlFor="referencia" className="block text-sm font-medium mb-1">
                Referência / Responsável (opcional)
              </label>
              <input
                type="text"
                id="referencia"
                className="input w-full"
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
                placeholder="Ex: Indicado por João, Amigo do time, etc."
              />
              <p className="text-sm text-gray-500 mt-1">
                Como você conheceu este time ou quem te indicou
              </p>
            </div>

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={submitting}
            >
              {submitting ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Cadastrando...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <User className="w-4 h-4" />
                  <span>Cadastrar Jogador</span>
                </div>
              )}
            </button>
          </form>
        </div>

        {/* Informações do Grupo */}
        <div className="card p-6 mt-6">
          <h2 className="text-lg font-bold mb-4">Sobre o Grupo</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                <Trophy className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Nome do Grupo</p>
                <p className="font-medium">{group.nome}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Star className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Tipo de Campo</p>
                <p className="font-medium capitalize">{group.tipo}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white dark:bg-dark-paper border-t dark:border-dark-border mt-8">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center">
          <div className="flex items-center justify-center gap-2 text-primary-600 dark:text-primary-400 mb-2">
            <Trophy className="w-5 h-5" />
            <span className="font-medium">Presida.Club</span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Gestão financeira para times de futebol amador
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicPlayerRegistrationPage;